import { useTranslation } from 'react-i18next';
import { UseFormRegister, Path } from 'react-hook-form';
import type { SaveCompanyDto } from '@monorepo/shared';
import { FormInput } from '../ui/FormInput';
import { FormCheckbox } from '../ui/FormCheckbox';
import { VALIDATION_PATTERNS } from '../../constants/validation';

interface DataProtectionErrors {
  dpoEmail?: { message?: string };
  dataProtectionRepresentativeEmail?: { message?: string };
  externalConsultantEmail?: { message?: string };
}

interface DpoFormProps<T extends SaveCompanyDto> {
  register: UseFormRegister<T>;
  errors: DataProtectionErrors;
  hasDpoDeclaration: boolean;
  hasDataProtectionRepresentative: boolean;
  isExternalConsultant: boolean;
}

export const DpoForm = <T extends SaveCompanyDto>({
  register,
  errors,
  hasDpoDeclaration,
  hasDataProtectionRepresentative,
  isExternalConsultant,
}: DpoFormProps<T>) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-text border-b pb-2">{t('company.section.dpo')}</h2>

      <p className="text-sm text-secondary">{t('company.section.dpo_subtitle')}</p>

      <FormCheckbox
        label={t('company.has_dpo_declaration.label')}
        {...register('dataProtectionSettings.hasDpoDeclaration' as Path<T>)}
      />

      {hasDpoDeclaration && (
        <FormInput
          label={t('company.dpo_email.label')}
          type="email"
          placeholder={t('company.dpo_email.placeholder')}
          error={errors.dpoEmail?.message}
          required
          {...register('dataProtectionSettings.dpoEmail' as Path<T>, {
            required: hasDpoDeclaration ? t('company.dpo_email.error.required') : false,
            pattern: {
              value: VALIDATION_PATTERNS.EMAIL,
              message: t('company.dpo_email.error.invalid'),
            },
          })}
        />
      )}

      <FormCheckbox
        label={t('company.has_data_protection_representative.label')}
        {...register('dataProtectionSettings.hasDataProtectionRepresentative' as Path<T>)}
      />

      {hasDataProtectionRepresentative && (
        <FormInput
          label={t('company.data_protection_representative_email.label')}
          type="email"
          required
          placeholder={t('company.data_protection_representative_email.placeholder')}
          error={errors.dataProtectionRepresentativeEmail?.message}
          {...register('dataProtectionSettings.dataProtectionRepresentativeEmail' as Path<T>, {
            required: hasDataProtectionRepresentative
              ? t('company.data_protection_representative_email.error.required')
              : false,
            pattern: {
              value: VALIDATION_PATTERNS.EMAIL,
              message: t('company.data_protection_representative_email.error.invalid'),
            },
          })}
        />
      )}

      <FormCheckbox
        label={t('company.is_external_consultant.label')}
        {...register('dataProtectionSettings.isExternalConsultant' as Path<T>)}
      />

      {isExternalConsultant && (
        <FormInput
          label={t('company.external_consultant_email.label')}
          type="email"
          required
          placeholder={t('company.external_consultant_email.placeholder')}
          error={errors.externalConsultantEmail?.message}
          {...register('dataProtectionSettings.externalConsultantEmail' as Path<T>, {
            required: isExternalConsultant
              ? t('company.external_consultant_email.error.required')
              : false,
            pattern: {
              value: VALIDATION_PATTERNS.EMAIL,
              message: t('company.external_consultant_email.error.invalid'),
            },
          })}
        />
      )}
    </div>
  );
};
